import { useState, useEffect } from 'react'
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore'
import { db } from '../../config/firebase'
import { useAuthStore } from '../../store/authStore'
import { useNotifications } from '../../contexts/NotificationContext'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Trash2, MessageSquare, Megaphone, Bell, CheckCircle2, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import EmptyState from '../ui/EmptyState'

export default function NotificationHistoryTab() {
    const { user, userRole, currentOrg } = useAuthStore()
    const { notifications: receivedNotifications } = useNotifications() // Using context for received
    const [sentBroadcasts, setSentBroadcasts] = useState<any[]>([])
    const [activeSubTab, setActiveSubTab] = useState<'received' | 'sent'>('received')
    const [loadingSent, setLoadingSent] = useState(false)
    const [selectedImage, setSelectedImage] = useState<string | null>(null)

    const isAdmin = userRole === 'admin' || userRole === 'owner' || currentOrg?.ownerId === user?.uid

    // Load Sent Broadcasts for Admins
    useEffect(() => {
        if (isAdmin && activeSubTab === 'sent' && currentOrg) {
            fetchSentBroadcasts()
        }
    }, [isAdmin, activeSubTab, currentOrg])

    const fetchSentBroadcasts = async () => {
        setLoadingSent(true)
        try {
            // Check if BroadcastTab is writing to 'notifications_broadcast'
            const q = query(
                collection(db, 'notifications_broadcast'),
                orderBy('createdAt', 'desc')
                // Note: If we had orgId on broadcast docs, we'd filter by it.
                // Assuming global or single-tenant for now, or that the collection is right.
            )
            const snapshot = await getDocs(q)
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
            setSentBroadcasts(data)
        } catch (error) {
            console.error("Error fetching sent broadcasts:", error)
            toast.error("Erro ao carregar histórico de envios")
        } finally {
            setLoadingSent(false)
        }
    }

    const handleDeleteReceived = async (id: string, e: React.MouseEvent, isBroadcast: boolean = false) => {
        e.stopPropagation()
        if (!user) return
        try {
            if (isBroadcast) {
                // Determine if it was a broadcast by checking argument or logic
                // Implementing soft delete for broadcast
                // We need to import setDoc and serverTimestamp
                const { setDoc, serverTimestamp } = await import('firebase/firestore')
                await setDoc(doc(db, 'users', user.uid, 'hidden_broadcasts', id), {
                    hiddenAt: serverTimestamp()
                })
            } else {
                await deleteDoc(doc(db, 'users', user.uid, 'notifications', id))
            }
            toast.success("Notificação removida")
        } catch (error) {
            console.error(error)
            toast.error("Erro ao remover")
        }
    }

    const handleDeleteSent = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation()
        try {
            await deleteDoc(doc(db, 'notifications_broadcast', id))
            setSentBroadcasts(prev => prev.filter(item => item.id !== id))
            toast.success("Transmissão removida do histórico")
        } catch (error) {
            console.error(error)
            toast.error("Erro ao remover")
        }
    }

    const formatDate = (date: any) => {
        if (!date) return '-'
        // Handle Firestore Timestamp or JS Date or ISO string
        const d = date?.toDate ? date.toDate() : (date instanceof Date ? date : new Date(date))
        return format(d, "dd 'de' MMM 'às' HH:mm", { locale: ptBR })
    }

    return (
        <div className="bg-white rounded-[2rem] border border-[#001c28]/5 shadow-xl shadow-[#001c28]/5 overflow-hidden flex flex-col h-[600px]">
            {/* Header / Tabs */}
            <div className="p-6 border-b border-[#001c28]/5 flex justify-between items-center bg-gray-50/50">
                <div className="flex items-center gap-4">
                    <h2 className="text-lg font-black text-[#001c28] uppercase tracking-wider flex items-center gap-2">
                        <Bell className="text-[#d4a94d]" />
                        Histórico
                    </h2>

                    {isAdmin && (
                        <div className="flex bg-gray-200 rounded-lg p-1">
                            <button
                                onClick={() => setActiveSubTab('received')}
                                className={`px-4 py-1.5 rounded-md text-xs font-bold uppercase transition-all ${activeSubTab === 'received'
                                    ? 'bg-white shadow text-[#001c28]'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                Recebidas
                            </button>
                            <button
                                onClick={() => setActiveSubTab('sent')}
                                className={`px-4 py-1.5 rounded-md text-xs font-bold uppercase transition-all ${activeSubTab === 'sent'
                                    ? 'bg-white shadow text-[#001c28]'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                Enviadas
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">

                {/* RECEIVED LIST */}
                {activeSubTab === 'received' && (
                    <div className="space-y-4">
                        {receivedNotifications.length === 0 ? (
                            <EmptyState
                                icon={CheckCircle2}
                                title="Tudo limpo!"
                                description="Seu histórico de notificações está vazio."
                            />
                        ) : (
                            receivedNotifications.map(notification => (
                                <div key={notification.id} className="bg-white p-4 rounded-2xl border border-[#001c28]/5 shadow-sm hover:shadow-md transition-all group relative">
                                    <div className="flex items-start gap-4">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${notification.read ? 'bg-gray-100 text-gray-400' : 'bg-[#d4a94d]/20 text-[#d4a94d]'}`}>
                                            {notification.type === 'new_message' ? <MessageSquare size={20} /> : <Bell size={20} />}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="min-w-0 flex-1">
                                                    <h4 className={`text-sm font-bold truncate ${notification.read ? 'text-gray-600' : 'text-[#001c28]'}`}>
                                                        {notification.title}
                                                    </h4>
                                                    <p className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-2">
                                                        {notification.message}
                                                    </p>
                                                    {(notification as any).imageUrl && (
                                                        <div className="mt-2">
                                                            <span className="text-[10px] text-[#d4a94d] font-bold flex items-center gap-1 mb-1">
                                                                📷 Imagem Anexada
                                                            </span>
                                                            <img
                                                                src={(notification as any).imageUrl}
                                                                alt="Anexo"
                                                                className="w-full h-32 object-cover rounded-lg border border-gray-100 cursor-zoom-in hover:opacity-90 transition-opacity"
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    setSelectedImage((notification as any).imageUrl)
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="flex flex-col items-end gap-2 shrink-0">
                                                    <span className="text-[10px] text-gray-400 font-medium whitespace-nowrap">
                                                        {formatDate(notification.createdAt)}
                                                    </span>
                                                    <button
                                                        onClick={(e) => handleDeleteReceived(notification.id, e, (notification as any).targetGroup ? true : false)}
                                                        className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                        title="Excluir"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* SENT LIST (Broacasts) */}
                {activeSubTab === 'sent' && (
                    <div className="space-y-4">
                        {loadingSent ? (
                            <div className="text-center p-8 text-gray-400">Carregando histórico...</div>
                        ) : sentBroadcasts.length === 0 ? (
                            <EmptyState
                                icon={Megaphone}
                                title="Nenhuma transmissão"
                                description="Você ainda não enviou nenhum broadcast."
                            />
                        ) : (
                            sentBroadcasts.map(broadcast => (
                                <div key={broadcast.id} className="bg-white p-4 rounded-2xl border border-[#001c28]/5 shadow-sm hover:shadow-md transition-all group relative">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-[#001c28] text-white flex items-center justify-center shrink-0">
                                            <Megaphone size={20} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="min-w-0 flex-1">
                                                    <h4 className="text-sm font-bold text-[#001c28] truncate">
                                                        {broadcast.title}
                                                    </h4>
                                                    <p className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-2">
                                                        {broadcast.message}
                                                    </p>
                                                    <div className="mt-2 flex items-center gap-2">
                                                        <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-md font-bold uppercase tracking-wider">
                                                            Para: {broadcast.targetGroup === 'all' ? 'Todos' : broadcast.targetGroup}
                                                        </span>
                                                        {broadcast.imageUrl && (
                                                            <span className="text-[10px] text-[#d4a94d] font-bold flex items-center gap-1">
                                                                📷 Com Imagem
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex flex-col items-end gap-2 shrink-0">
                                                    <span className="text-[10px] text-gray-400 font-medium whitespace-nowrap">
                                                        {formatDate(broadcast.createdAt)}
                                                    </span>
                                                    <button
                                                        onClick={(e) => handleDeleteSent(broadcast.id, e)}
                                                        className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                        title="Excluir do Histórico"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

            </div>
        </div>
    )
}
